/**
 * Feature Flags Context
 *
 * - Defaults from env; then remote JSON (app-wide, no build); then localStorage overrides
 * - Fetch from getFeatureFlagsUrl() (e.g. /feature-flags.json or REACT_APP_FEATURE_FLAGS_URL)
 * - Cambiar el JSON en el servidor actualiza los flags para toda la app en la próxima carga
 */

import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import {
  FEATURE_FLAGS,
  FEATURE_FLAGS_STORAGE_KEY,
  getDefaultFlags,
  getFeatureFlagsUrl,
} from '../constants/featureFlags';
import { serviceLogger } from '../utils/serviceLogger';

const defaultFlags = getDefaultFlags();

const loadOverrides = () => {
  try {
    const raw = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const saveOverrides = (overrides) => {
  try {
    localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(overrides));
  } catch (e) {
    serviceLogger.warn('FeatureFlags: could not persist overrides', e);
  }
};

/** Normalize remote JSON: only allow known keys and booleans */
const parseRemoteFlags = (data) => {
  if (!data || typeof data !== 'object') return {};
  const validKeys = Object.values(FEATURE_FLAGS);
  const out = {};
  validKeys.forEach((key) => {
    if (key in data && typeof data[key] === 'boolean') out[key] = data[key];
  });
  return out;
};

const FeatureFlagsContext = createContext({
  flags: defaultFlags,
  isEnabled: () => true,
  setFlag: () => {},
  resetOverrides: () => {},
});

export const FeatureFlagsProvider = ({ children }) => {
  const [overrides, setOverrides] = useState(loadOverrides);
  const [remoteFlags, setRemoteFlags] = useState({});

  useEffect(() => {
    const url = getFeatureFlagsUrl();
    if (!url) return;
    fetch(url, { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(res.statusText))))
      .then((data) => setRemoteFlags(parseRemoteFlags(data)))
      .catch(() => {});
  }, []);

  const flags = useMemo(
    () => ({ ...defaultFlags, ...remoteFlags, ...overrides }),
    [remoteFlags, overrides],
  );

  const isEnabled = useCallback(
    (key) => {
      const value = flags[key];
      return value === true;
    },
    [flags],
  );

  const setFlag = useCallback((key, value) => {
    if (!Object.values(FEATURE_FLAGS).includes(key)) return;
    setOverrides((prev) => {
      const next = { ...prev, [key]: !!value };
      saveOverrides(next);
      return next;
    });
  }, []);

  const resetOverrides = useCallback(() => {
    setOverrides({});
    saveOverrides({});
  }, []);

  const value = useMemo(
    () => ({ flags, isEnabled, setFlag, resetOverrides }),
    [flags, isEnabled, setFlag, resetOverrides],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export function useFeatureFlags() {
  const context = React.useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  }
  return context;
}

/**
 * Convenience hook for a single flag
 * @param {keyof typeof FEATURE_FLAGS} key
 * @returns {boolean}
 */
export function useFeatureFlag(key) {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(key);
}
